/**
 * defaultUserSettingsService — the theme / language / AI-personalization a
 * tenant hands a user who has never chosen.
 *
 * Three verbs, kept apart on purpose (the API keeps them apart for the same
 * reason): saving a default decides what NEW people get, applying to users
 * overwrites what EXISTING people already picked, and applying to descendants
 * overwrites what SUB-TENANTS have configured. Bundling them would make a
 * routine save silently reach into other people's settings.
 */

import api from '@/services/api';

export type DefaultSettingKey = 'theme' | 'language' | 'ai_personalization';

/** Every key present — this is what a user actually gets. */
export interface ResolvedUserDefaults {
  theme: 'light' | 'dark';
  language: 'en' | 'he';
  ai_personalization: boolean;
}

/** Only what THIS tenant has decided; a missing key means "inherit". */
export type OwnUserDefaults = Partial<ResolvedUserDefaults>;

/** Where a resolved value came from, so the editor can say so. */
export type DefaultSource =
  | { kind: 'own' }
  | { kind: 'platform' }
  | { kind: 'inherited'; tenant_id: string; tenant_name: string };

export interface DefaultUserSettingsPayload {
  default_user_settings: {
    own: OwnUserDefaults;
    resolved: ResolvedUserDefaults;
    sources: Record<DefaultSettingKey, DefaultSource>;
  };
  platform_defaults: ResolvedUserDefaults;
  cascade: {
    enforced: boolean;
    has_children: boolean;
    differs_from_parent: boolean;
    parent_enforced: boolean;
    parent: { tenant_id: string; tenant_name: string } | null;
  };
  user_counts: { tenant: number; subtree: number };
}

class DefaultUserSettingsService {
  async get(tenantId: string): Promise<DefaultUserSettingsPayload> {
    const { data } = await api.get(`/super-admin/tenants/${tenantId}/default-user-settings`);
    return data;
  }

  /**
   * Replace this tenant's own defaults. `null` for a key means "inherit from
   * my parent" — send it explicitly rather than omitting the key, so clearing
   * a value is a statement rather than an accident of serialisation.
   */
  async save(
    tenantId: string,
    values: Record<DefaultSettingKey, string | boolean | null>,
  ): Promise<DefaultUserSettingsPayload> {
    const { data } = await api.put(
      `/super-admin/tenants/${tenantId}/default-user-settings`,
      values,
    );
    return data;
  }

  /** Overwrite what people in this tenant have ALREADY chosen. */
  async applyToUsers(
    tenantId: string,
    opts: { keys?: DefaultSettingKey[]; includeDescendants?: boolean; dryRun?: boolean } = {},
  ): Promise<{ users_written?: number; users_in_scope: number; dry_run?: boolean }> {
    const { data } = await api.post(
      `/super-admin/tenants/${tenantId}/default-user-settings/apply-to-users`,
      {
        keys: opts.keys,
        include_descendants: !!opts.includeDescendants,
        dry_run: !!opts.dryRun,
      },
    );
    return data;
  }

  /** Copy this tenant's defaults onto every sub-tenant, replacing theirs. */
  async applyToDescendants(
    tenantId: string,
    dryRun = false,
  ): Promise<{ count: number; targets: Array<{ tenant_id: string; tenant_name: string }> }> {
    const { data } = await api.post(
      `/super-admin/tenants/${tenantId}/default-user-settings/apply-to-descendants`,
      { dry_run: dryRun },
    );
    return data;
  }

  /** "Descendants inherit my changes automatically." */
  async setEnforcement(
    tenantId: string,
    enforced: boolean,
  ): Promise<{ enforced: boolean; descendants_updated: number }> {
    const { data } = await api.patch(
      `/super-admin/tenants/${tenantId}/default-user-settings/enforcement`,
      { enforced },
    );
    return data;
  }
}

export const defaultUserSettingsService = new DefaultUserSettingsService();
export default defaultUserSettingsService;
